// SL.L2 (#2958) — client for SL.B1's product backlog API (routes/backlog.ts),
// with C1's re-estimate and plan-next, and G1's spike outcome.
import { apiFetch } from './api';
import type { Sprint } from './sprintsApi';

export type BacklogItemType = 'US' | 'TECH' | 'BUG' | 'SPIKE' | 'INFRA';

export interface BacklogItem {
  id: string;
  /** Exactly as stored: US-001, US-044a. Never reformat. */
  key: string;
  type: BacklogItemType;
  aliases: string[];
  state: string;
  /** Postgres numeric as a string ('1.5'). Never parsed: the server computes ranks. */
  rank: string;
  source: 'user_stories' | 'direct';
  notInRequirements: boolean;
  requirementsChanged: boolean;
  sprintId: string | null;
  statement: string;
  acceptanceCriteria: string[];
  /** Story points, a whole number 0–1000; null until estimated. */
  estimatePoints: number | null;
  /** A SPIKE's outcome (G1); written only through recordSpikeOutcome. */
  outcomeSummary: string | null;
  outcomeAdrId: string | null;
  /** This item's actions (C1, G1): re_estimate, record_outcome. */
  allowedActions: string[];
  createdAt: string;
}

export interface BacklogPayload {
  items: BacklogItem[];
  allowedActions: string[];
  /**
   * SL.B2 (#2956): set when the backlog failed to re-parse after a
   * requirements amendment. B1 does not return it yet; absent or null means
   * in sync. Field name proposed on #2958 (6230052180, open point A).
   */
  outOfSync?: { failedHeadings: string[] } | null;
}

export interface NewBacklogItem {
  type: BacklogItemType;
  statement: string;
  acceptanceCriteria: string[];
}

// Ids come from the URL (useParams decodes them): encode them back, so a
// crafted link can't turn `..%2F` into a different API path (R2 P3-2).
const enc = encodeURIComponent;

export function fetchBacklog(projectId: string): Promise<BacklogPayload> {
  return apiFetch<BacklogPayload>(`/projects/${enc(projectId)}/backlog`);
}

export function addBacklogItem(projectId: string, item: NewBacklogItem): Promise<BacklogItem> {
  return apiFetch<BacklogItem>(`/projects/${enc(projectId)}/backlog/items`, {
    method: 'POST',
    body: JSON.stringify(item),
  });
}

/** Place `itemId` between two adjacent items (null: the top or the bottom). */
export function reorderBacklogItem(
  projectId: string,
  itemId: string,
  neighbours: { afterItemId: string | null; beforeItemId: string | null },
): Promise<BacklogItem> {
  return apiFetch<BacklogItem>(`/projects/${enc(projectId)}/backlog/items/${enc(itemId)}/reorder`, {
    method: 'POST',
    body: JSON.stringify(neighbours),
  });
}

/** Set a whole-number estimate (0–1000). 409 while the item is in an approved or active sprint. */
export function reEstimateBacklogItem(projectId: string, itemId: string, estimatePoints: number): Promise<unknown> {
  return apiFetch(`/projects/${enc(projectId)}/backlog/items/${enc(itemId)}/re-estimate`, {
    method: 'POST',
    body: JSON.stringify({ estimatePoints }),
  });
}

/**
 * A SPIKE's outcome. `outcomeAdrId` left out keeps the current ADR link;
 * null clears it (G1, routes/backlog.ts outcomeBody).
 */
export interface SpikeOutcomeBody {
  outcomeSummary: string;
  outcomeAdrId?: string | null;
}

export function recordSpikeOutcome(projectId: string, itemId: string, body: SpikeOutcomeBody): Promise<unknown> {
  return apiFetch(`/projects/${enc(projectId)}/backlog/items/${enc(itemId)}/outcome`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * GET /sprints: the backlog page reads the project-level actions (plan_next);
 * the board (SL.L1a-1, sprintsApi.ts) reads the sprints.
 */
export interface SprintsPayload {
  sprints: Sprint[];
  allowedActions: string[];
}

export function fetchSprints(projectId: string): Promise<SprintsPayload> {
  return apiFetch<SprintsPayload>(`/projects/${enc(projectId)}/sprints`);
}

/** C1's create-or-resume: `created` is false when a waiting draft was returned. */
export interface PlanNextResult {
  sprint: { id: string; number: number };
  created: boolean;
  /** SL.D1 (#2951): the plan a draft with none just adopted (the newest unlinked one saved in Phase 4), or null. */
  adoptedPlan?: { artefactId: string; title: string; version: string } | null;
}

export function planNextSprint(projectId: string): Promise<PlanNextResult> {
  return apiFetch<PlanNextResult>(`/projects/${enc(projectId)}/sprints/plan-next`, {
    method: 'POST',
    body: '{}',
  });
}

/**
 * The neighbours to send for moving `activeId` to `overId`'s position in
 * `ids` (the server's current order). Null when the move changes nothing.
 * Both neighbours are named, so the server refuses (400) if its order has
 * moved on since `ids` was read, instead of placing the item somewhere else.
 */
export function neighboursForMove(
  ids: readonly string[],
  activeId: string,
  overId: string,
): { afterItemId: string | null; beforeItemId: string | null } | null {
  const from = ids.indexOf(activeId);
  const to = ids.indexOf(overId);
  if (from < 0 || to < 0 || from === to) return null;
  const rest = ids.filter((id) => id !== activeId);
  // Removing the item shifts later positions up by one; `to` is where it lands.
  return { afterItemId: rest[to - 1] ?? null, beforeItemId: rest[to] ?? null };
}
